import Singleton from '../common/Singleton';
import GeometryAlgorithm from '../geometry/GeometryAlgorithm';

/**
 * 该类是一个单例类，提供了有关图幅计算的各种方法，包括根据经纬度计算图幅号；根据图幅号计算图幅范围；点位和图幅的关系等。
 * 此类的计算精度位5位小数
 */
export default class MeshAlgorithm extends Singleton {
    /**
     * 初始化构造函数.
     * @returns {undefined}
     */
    constructor() {
        super();

        this.geomAlg = GeometryAlgorithm.getInstance();
    }

    /**
     * 矫正经纬度
     * 解决东经180°计算错误的问题
     * @param  {[type]} lon [description]
     * @param  {[type]} lat [description]
     * @return {[type]}     [description]
     */
    _rectify(lon, lat) {
        let oLon = lon;
        let oLat = lat;

        if (lon >= 180) {
            oLon -= 360;
        }
        oLon += 180;
        oLat += 180;

        return [oLon, oLat];
    }

    /**
     * 将度转换成秒（1度=3600秒），并将小数转换为大整数
     * @param   {number} degree 经/纬度
     * @param   {number} scale  几何精度，小数点后的尾数
     * @return  {number}        大整数
     * @private
     */
    _getIntSecond (degree, scale = this.geomAlg.precious) {
        // 1度=3600秒，转秒时已经解决了2位精度的小数，因此处理精度时可以减去2位
        const base = 3600 * Math.pow(10, scale - 2);

        return Math.round(degree * base);
    }

    /**
     * 将秒转换为处理小数精度后的大整数
     * @param   {number} second 秒数
     * @param   {number} scale  几何精度，小数点后的尾数
     * @return  {number}        大整数
     * @private
     */
    _secondToRadix (second, scale = this.geomAlg.precious) {
        // 由于秒本身已经解决了2位精度的小数，这一步只需解决剩余的即可
        return second * Math.pow(10, scale - 2);
    }

    /**
     * 是否在20w图幅框上
     * @param  {number} lon 经度
     * @param  {number} lat 纬度
     * @return {Boolean}    是否在图幅边线上
     */
    isPointAtMeshBorder20w(lon, lat) {
        let model = 0;
        let step = this._secondToRadix(2400);
        const longLat = this._getIntSecond(lat);
        let remainder = longLat % step;
        const rowIndex = parseInt(longLat / step);

        switch (rowIndex % 3) {
            case 0: // 第一个底部不变，顶部向上
                if (remainder === 0) {
                    model |= 0x01;
                }
                break;
            case 1: // 第二个底部向上，顶部向下
                // 余数距离上框等于0.012秒
                if (step - remainder === 12) {
                    model |= 0x01;
                } else if (remainder <= 12) {
                    model |= 0x01;
                }
                break;
            case 2: // 第三个底部向下，顶部不变
                if (remainder === step) {
                    // 余数距离上框等于0秒
                    model |= 0x01;
                }
                break;
            default:
                break;
        }

        // 为避免浮点数的内存影响，将秒*10的三次方(由于0.00001度为0.036秒)
        const longLon = this._getIntSecond(lon);
        step = this._secondToRadix(3600);
        remainder = longLon % step;
        if (remainder === 0) {
            model |= 0x10;
        }

        if ((model & 0x0f) === 0x01) {
            return true;
        }
        return false;
    }

    /**
     * 是否在2.5w图幅框上
     * @param  {number} lon 经度
     * @param  {number} lat 纬度
     * @return {Boolean}    是否在图幅边线上
     */
    isPointAtMeshBorder25k(lon, lat) {
        const longY = this._getIntSecond(lat);
        const step = this._secondToRadix(300);
        const yt = parseInt(longY / step);
        const remainder = longY % step;

        // 判断在图幅线上的情况
        if (remainder === 0) { // 直接在理想行号图廓线上，0,0.25,0.5,1,...
            return true;
        } else if (remainder <= 12) { // 距离理想行号下图廓线距离
            if (yt % 3 === 2) {
                // 处于横轴图廓线上
                return true;
            }
        } else if ((step - remainder) <= 12) { // 距离理想行号上图廓线距离
            if (yt % 3 === 0) {
                // 处于图廓线上
                return true;
            }
        }

        return false;
    }

    /**
     * 判断是否在5k图幅框上
     * @param  {number} lon 经度
     * @param  {number} lat 纬度
     * @return {Boolean}    是否在图幅边线上
     */
    isPointAtMeshBorder5k(lon, lat) {
        const longY = this._getIntSecond(lat);
        const step = this._secondToRadix(75);
        const yt = parseInt(longY / step);
        const remainder = longY % step;

        // 判断在图幅线上的情况
        if (remainder === 0) { // 直接在理想行号图廓线上，0,0.25,0.5,1,...
            return true;
        } else if (remainder <= 12) { // 距离理想行号下图廓线距离
            if (yt % 3 === 2) {
                // 处于横轴图廓线上
                return true;
            }
        } else if ((step - remainder) <= 12) { // 距离理想行号上图廓线距离
            if (yt % 3 === 0) {
                // 处于图廓线上
                return true;
            }
        }

        return false;
    }

    /**
     * 计算2.5w图幅号，只返回右上图幅
     * @param  {number} lon 经度
     * @param  {number} lat 纬度
     * @return {string}     图幅号
     */
    point2Mesh25k(lon, lat) {
        let oLon = lon;
        let oLat = lat;

        if (this.isPointAtMeshBorder25k(lon, lat)) { // 返回右上图幅
            oLat += 0.00001;
        }

        [oLon, oLat] = this._rectify(oLon, oLat);

        const longX = this._getIntSecond(oLon);
        const longY = this._getIntSecond(oLat);
        const M1M2 = parseInt(longY / this._secondToRadix(14400)) + 1;
        const M3M4 = parseInt(longX / this._secondToRadix(21600)) + 1;
        const M5 = parseInt(longY / this._secondToRadix(2400)) % 6 + 1;
        const M6 = parseInt(longX / this._secondToRadix(3600)) % 6 + 1;
        const yt = parseInt(longY / this._secondToRadix(300));
        const M7 = yt % 8 + 1;
        const M8 = parseInt(longX / this._secondToRadix(450)) % 8 + 1;

        return this._constructMesh25k(M1M2, M3M4, M5, M6, M7, M8);
    }

    /**
     * 如果点在图幅框上，返回多个图幅号，且有顺序，按顺序为左下，右下，右上，左上
     * @param  {number} lon 经度
     * @param  {number} lat 纬度
     * @return {array}      图幅号数组
     */
    point2Meshes25k(lon, lat) {
        const [oLon, oLat] = this._rectify(lon, lat);

        const longX = this._getIntSecond(oLon);
        const longY = this._getIntSecond(oLat);
        const M1M2 = parseInt(longY / this._secondToRadix(14400)) + 1;
        const M3M4 = parseInt(longX / this._secondToRadix(21600)) + 1;
        const M5 = parseInt(longY / this._secondToRadix(2400)) % 6 + 1;
        const M6 = parseInt(longX / this._secondToRadix(3600)) % 6 + 1;
        const stepY = this._secondToRadix(300);
        const yt = parseInt(longY / stepY);
        const M7 = yt % 8 + 1;
        const stepX = this._secondToRadix(450);
        const M8 = parseInt(longX / stepX) % 8 + 1;

        let M7_bak = -999;
        let M8_bak = -999;
        const remainderY = longY % stepY;
        // 判断在图幅线上的情况
        if (remainderY === 0) { // 直接在理想行号图廓线上，0,0.25,0.5,1,...
            M7_bak = M7 - 1;
        } else if (remainderY <= 12) { // 距离理想行号下图廓线距离
            if (yt % 3 === 2) {
                // 处于横轴图廓线上
                M7_bak = M7 - 1;
            }
        } else if ((stepY - remainderY) <= 12) { // 距离理想行号上图廓线距离
            if (yt % 3 === 0) {
                // 处于图廓线上
                M7_bak = M7 + 1;
            }
        }
        // 经度坐标没有四舍五入，所以理论上只有=0和大于12的情况
        const remainderX = longX % stepX;
        if (remainderX <= 12) {
            M8_bak = M8 - 1;
        }

        let meshes;
        if (M7_bak > -999 && M8_bak > -999) { // 图廓点，4个图幅
            meshes = new Array(4);
            if (M7_bak < M7) {
                if (M8_bak < M8) {
                    meshes[0] = this._constructMesh25k(M1M2, M3M4, M5, M6, M7_bak, M8_bak);
                    meshes[1] = this._constructMesh25k(M1M2, M3M4, M5, M6, M7_bak, M8);
                    meshes[2] = this._constructMesh25k(M1M2, M3M4, M5, M6, M7, M8);
                    meshes[3] = this._constructMesh25k(M1M2, M3M4, M5, M6, M7, M8_bak);
                } else {
                    meshes[0] = this._constructMesh25k(M1M2, M3M4, M5, M6, M7_bak, M8);
                    meshes[1] = this._constructMesh25k(M1M2, M3M4, M5, M6, M7_bak, M8_bak);
                    meshes[2] = this._constructMesh25k(M1M2, M3M4, M5, M6, M7, M8_bak);
                    meshes[3] = this._constructMesh25k(M1M2, M3M4, M5, M6, M7, M8);
                }
            } else {
                if (M8_bak < M8) {
                    meshes[0] = this._constructMesh25k(M1M2, M3M4, M5, M6, M7, M8_bak);
                    meshes[1] = this._constructMesh25k(M1M2, M3M4, M5, M6, M7, M8);
                    meshes[2] = this._constructMesh25k(M1M2, M3M4, M5, M6, M7_bak, M8);
                    meshes[3] = this._constructMesh25k(M1M2, M3M4, M5, M6, M7_bak, M8_bak);
                } else {
                    meshes[0] = this._constructMesh25k(M1M2, M3M4, M5, M6, M7, M8);
                    meshes[1] = this._constructMesh25k(M1M2, M3M4, M5, M6, M7, M8_bak);
                    meshes[2] = this._constructMesh25k(M1M2, M3M4, M5, M6, M7_bak, M8_bak);
                    meshes[3] = this._constructMesh25k(M1M2, M3M4, M5, M6, M7_bak, M8);
                }
            }
        } else if (M7_bak > -999) {
            meshes = new Array(2);
            if (M7_bak < M7) {
                meshes[0] = this._constructMesh25k(M1M2, M3M4, M5, M6, M7_bak, M8);
                meshes[1] = this._constructMesh25k(M1M2, M3M4, M5, M6, M7, M8);
            } else {
                meshes[0] = this._constructMesh25k(M1M2, M3M4, M5, M6, M7, M8);
                meshes[1] = this._constructMesh25k(M1M2, M3M4, M5, M6, M7_bak, M8);
            }
        } else if (M8_bak > -999) {
            meshes = new Array(2);
            if (M8_bak < M8) {
                meshes[0] = this._constructMesh25k(M1M2, M3M4, M5, M6, M7, M8_bak);
                meshes[1] = this._constructMesh25k(M1M2, M3M4, M5, M6, M7, M8);
            } else {
                meshes[0] = this._constructMesh25k(M1M2, M3M4, M5, M6, M7, M8);
                meshes[1] = this._constructMesh25k(M1M2, M3M4, M5, M6, M7, M8_bak);
            }
        } else {
            meshes = [this._constructMesh25k(M1M2, M3M4, M5, M6, M7, M8)];
        }

        return meshes;
    }

    /**
     * 计算100w图幅号，只返回右上图幅
     * @param  {number} lon 经度
     * @param  {number} lat 纬度
     * @return {string}     图幅号
     */
    point2Mesh100w(lon, lat) {
        const [oLon, oLat] = this._rectify(lon, lat);

        const longX = this._getIntSecond(oLon);
        const longY = this._getIntSecond(oLat);
        const M1M2 = parseInt(longY / this._secondToRadix(14400)) + 1;
        const M3M4 = parseInt(longX / this._secondToRadix(21600)) + 1;

        return `${M1M2}${M3M4}`;
    }

    /**
     * 计算20w图幅号，只返回右上图幅
     * @param  {number} lon 经度
     * @param  {number} lat 纬度
     * @return {string}     图幅号
     */
    point2Mesh20w(lon, lat) {
        let oLon = lon;
        let oLat = lat;

        if (this.isPointAtMeshBorder20w(lon, lat)) { // 返回右上图幅
            oLat += 0.00001;
        }

        [oLon, oLat] = this._rectify(oLon, oLat);

        const longX = this._getIntSecond(oLon);
        const longY = this._getIntSecond(oLat);
        const M1M2 = parseInt(longY / this._secondToRadix(14400)) + 1;
        const M3M4 = parseInt(longX / this._secondToRadix(21600)) + 1;
        const M5 = parseInt(longY / this._secondToRadix(2400)) % 6 + 1;
        const M6 = parseInt(longX / this._secondToRadix(3600)) % 6 + 1;

        return `${M1M2}${M3M4}${M5}${M6}`;
    }

    /**
     * 计算5k图幅号，只返回右上图幅
     * @param  {number} lon 经度
     * @param  {number} lat 纬度
     * @return {string}     图幅号
     */
    point2Mesh5k(lon, lat) {
        let oLon = lon;
        let oLat = lat;

        if (this.isPointAtMeshBorder5k(lon, lat)) { // 返回右上图幅
            oLat += 0.00001;
        }

        [oLon, oLat] = this._rectify(oLon, oLat);

        const longX = this._getIntSecond(oLon);
        const longY = this._getIntSecond(oLat);
        const M1M2 = parseInt(longY / this._secondToRadix(14400)) + 1;
        const M3M4 = parseInt(longX / this._secondToRadix(21600)) + 1;
        const M5 = parseInt(longY / this._secondToRadix(2400)) % 6 + 1;
        const M6 = parseInt(longX / this._secondToRadix(3600)) % 6 + 1;
        const M7 = parseInt(longY / this._secondToRadix(300)) % 8 + 1;
        const M8 = parseInt(longX / this._secondToRadix(450)) % 8 + 1;
        const M9 = parseInt(longY / this._secondToRadix(75)) % 4 + 1;
        const M10 = parseInt(longX / this._secondToRadix(112.5)) % 4 + 1;

        return `${M1M2}${M3M4}${M5}${M6}${M7}${M8}${M9}${M10}`;
    }

    /**
     * 计算2.5w图幅的四个角点坐标
     * @param  {string} mesh  图幅号字符串
     * @return {array}        坐标数组
     */
    mesh2Rect25k(mesh) {
        const M1M2 = parseInt(mesh.substring(0, 2));
        const M3M4 = parseInt(mesh.substring(2, 4));
        const M5 = parseInt(mesh.substring(4, 5));
        const M6 = parseInt(mesh.substring(5, 6));
        const M7 = parseInt(mesh.substring(6, 7));
        const M8 = parseInt(mesh.substring(7));
        const xmin = (M3M4 - 1) * 6 + (M6 - 1) + (M8 - 1) / 8.0 - 180;
        const ymin = (M1M2 - 1) * 4 + (M5 - 1) / 1.5 + (M7 - 1) / 12.0 - 180;
        const xmax = xmin + 0.125;
        const ymax = ymin + 1.0 / 12;

        return [
            this.geomAlg.precision(xmin),
            this.geomAlg.precision(ymin),
            this.geomAlg.precision(xmax),
            this.geomAlg.precision(ymax)
        ];
    }

    /**
     * 计算100w图幅的四个角点坐标
     * @param  {string} mesh  图幅号字符串
     * @return {array}        坐标数组
     */
    mesh2Rect100w(mesh) {
        const M1M2 = parseInt(mesh.substring(0, 2));
        const M3M4 = parseInt(mesh.substring(2, 4));
        const xmin = (M3M4 - 1) * 6 - 180;
        const ymin = (M1M2 - 1) * 4 - 180;
        const xmax = xmin + 6;
        const ymax = ymin + 4;

        return [
            this.geomAlg.precision(xmin),
            this.geomAlg.precision(ymin),
            this.geomAlg.precision(xmax),
            this.geomAlg.precision(ymax)
        ];
    }

    /**
     * 计算20w图幅的四个角点坐标
     * @param  {[type]} mesh [description]
     * @return {[type]}      [description]
     */
    mesh2Rect20w(mesh) {
        const M1M2 = parseInt(mesh.substring(0, 2));
        const M3M4 = parseInt(mesh.substring(2, 4));
        const M5 = parseInt(mesh.substring(4, 5));
        const M6 = parseInt(mesh.substring(5));
        const xmin = (M3M4 - 1) * 6 + (M6 - 1) - 180;
        const ymin = (M1M2 - 1) * 4 + (M5 - 1) / 1.5 - 180;
        const xmax = xmin + 1;
        const ymax = ymin + 2.0 / 3;

        return [
            this.geomAlg.precision(xmin),
            this.geomAlg.precision(ymin),
            this.geomAlg.precision(xmax),
            this.geomAlg.precision(ymax)
        ];
    }

    /**
     * 计算5k图幅的四个角点坐标
     * @param  {[type]} mesh [description]
     * @return {[type]}      [description]
     */
    mesh2Rect5k(mesh) {
        const a = parseInt(mesh.substring(0, 2));
        const b = parseInt(mesh.substring(2, 4));
        const c = parseInt(mesh.substring(4, 5));
        const d = parseInt(mesh.substring(5, 6));
        const e = parseInt(mesh.substring(6, 7));
        const f = parseInt(mesh.substring(7, 8));
        const g = parseInt(mesh.substring(8, 9));
        const h = parseInt(mesh.substring(9));
        const xmin = (b - 1) * 6 + (d - 1) + (f - 1) / 8.0 + (h - 1) / 32.0 - 180;
        const ymin = (a - 1) * 4 + (c - 1) / 1.5 + (e - 1) / 12.0 + (g - 1) / 48.0 - 180;
        const xmax = xmin + 1 / 32.0;
        const ymax = ymin + 1 / 48.0;
        return [
            this.geomAlg.precision(xmin),
            this.geomAlg.precision(ymin),
            this.geomAlg.precision(xmax),
            this.geomAlg.precision(ymax)
        ];
    }

    /**
     * 计算某图幅周报的9个图幅
     * [0]为本身图幅，从1-8的顺序为左下开始逆时针方向
     * @param  {string} mesh 图幅号
     * @return {array}       图幅号数组
     */
    get9NeighborMeshes25k(mesh) {
        const meshes = new Array(9);
        const M1M2 = parseInt(mesh.substring(0, 2));
        const M3M4 = parseInt(mesh.substring(2, 4));
        const M5 = parseInt(mesh.substring(4, 5));
        const M6 = parseInt(mesh.substring(5, 6));
        const M7 = parseInt(mesh.substring(6, 7));
        const M8 = parseInt(mesh.substring(7));

        meshes[0] = this._constructMesh25k(M1M2, M3M4, M5, M6, M7, M8);
        meshes[1] = this._constructMesh25k(M1M2, M3M4, M5, M6, M7 - 1, M8 - 1);
        meshes[2] = this._constructMesh25k(M1M2, M3M4, M5, M6, M7 - 1, M8);
        meshes[3] = this._constructMesh25k(M1M2, M3M4, M5, M6, M7 - 1, M8 + 1);
        meshes[4] = this._constructMesh25k(M1M2, M3M4, M5, M6, M7, M8 + 1);
        meshes[5] = this._constructMesh25k(M1M2, M3M4, M5, M6, M7 + 1, M8 + 1);
        meshes[6] = this._constructMesh25k(M1M2, M3M4, M5, M6, M7 + 1, M8);
        meshes[7] = this._constructMesh25k(M1M2, M3M4, M5, M6, M7 + 1, M8 - 1);
        meshes[8] = this._constructMesh25k(M1M2, M3M4, M5, M6, M7, M8 - 1);

        return meshes;
    }

    /**
     * 构造2.5w图幅号，进行进位或减位
     * @param   {[type]} M1M2 [description]
     * @param   {[type]} M3M4 [description]
     * @param   {[type]} M5   [description]
     * @param   {[type]} M6   [description]
     * @param   {[type]} M7   [description]
     * @param   {[type]} M8   [description]
     * @return  {[type]}      [description]
     * @private
     */
    _constructMesh25k(M1M2, M3M4, M5, M6, M7, M8) {
        let [oM1M2, oM3M4, oM5, oM6, oM7, oM8] = [M1M2, M3M4, M5, M6, M7, M8];

        if (oM7 > 8) {
            oM7 = 1;
            oM5++;
        }

        if (oM7 < 1) {
            oM7 = 8;
            oM5--;
        }

        if (oM8 > 8) {
            oM8 = 1;
            oM6++;
        }

        if (oM8 < 1) {
            oM8 = 8;
            oM6--;
        }

        if (oM5 > 6) {
            oM5 = 1;
            oM1M2++;
        }

        if (oM5 < 1) {
            oM5 = 6;
            oM1M2--;
        }

        if (oM6 > 6) {
            oM6 = 1;
            oM3M4++;
        }

        if (oM6 < 1) {
            oM6 = 6;
            oM3M4--;
        }

        return `${oM1M2}${oM3M4}${oM5}${oM6}${oM7}${oM8}`;
    }

    /**
     * 给定坐标范围（度），计算范围内包含的图幅
     * 两个点都在同一图幅的图廓线上，会计算属于这个图幅
     * @param  {number} lbX 左下角经度
     * @param  {number} lbY 左下角维度
     * @param  {number} rtX 右下角经度
     * @param  {number} rtY 右下角经度
     * @return {array}      图幅号数组
     */
    rect2Meshes25k(lbX, lbY, rtX, rtY) {
        // 截取精度
        const olbX = this.geomAlg.precision(lbX);
        const olbY = this.geomAlg.precision(lbY);
        const ortX = this.geomAlg.precision(rtX);
        const ortY = this.geomAlg.precision(rtY);

        let lbMesh;
        let rtMesh;
        // 计算左下坐标位于图幅
        const lbMeshes = this.point2Meshes25k(olbX, olbY);
        // 计算右上坐标位于图幅
        const rtMeshes = this.point2Meshes25k(ortX, ortY);
        if (olbX === ortX && olbY === ortY) { // 两点重合，取任意一点计算的图幅结果
            return lbMeshes;
        } else if (olbX === ortX) { // 两点垂直，当两点都在垂直图廓线上时，需要包含左右图幅
            lbMesh = lbMeshes.length > 1 ? (lbMeshes.length === 2 ? lbMeshes[0] : lbMeshes[3]) : lbMeshes[0];
            rtMesh = rtMeshes.length > 1 ? (rtMeshes.length === 2 ? rtMeshes[1] : rtMeshes[1]) : rtMeshes[0];
        } else if (olbY === ortY) { // 两点水平，当两点都在水平图廓线上时，需要包含上下图幅
            lbMesh = lbMeshes.length > 1 ? (lbMeshes.length === 2 ? lbMeshes[0] : lbMeshes[1]) : lbMeshes[0];
            rtMesh = rtMeshes.length > 1 ? (rtMeshes.length === 2 ? rtMeshes[1] : rtMeshes[3]) : rtMeshes[0];
        } else {
            lbMesh = lbMeshes.length > 1 ? (lbMeshes.length === 2 ? lbMeshes[1] : lbMeshes[2]) : lbMeshes[0];
            rtMesh = rtMeshes[0];
        }

        return this._getBetweenMeshes25k(lbMesh, rtMesh);
    }

    /**
     * 给定左下图幅，右上图幅，计算其外接矩形包含图幅
     * @param   {[type]} lbMesh [description]
     * @param   {[type]} rtMesh [description]
     * @return  {[type]}        [description]
     * @private
     */
    _getBetweenMeshes25k(lbMesh, rtMesh) {
        if (lbMesh === rtMesh) {
            return [lbMesh];
        }

        // 计算横纵向跨多少个图幅
        const lbM12 = parseInt(lbMesh.substring(0, 2));
        const lbM34 = parseInt(lbMesh.substring(2, 4));
        const lbM5 = parseInt(lbMesh.substring(4, 5));
        const lbM6 = parseInt(lbMesh.substring(5, 6));
        const lbM7 = parseInt(lbMesh.substring(6, 7));
        const lbM8 = parseInt(lbMesh.substring(7, 8));
        const rtM12 = parseInt(rtMesh.substring(0, 2));
        const rtM34 = parseInt(rtMesh.substring(2, 4));
        const rtM5 = parseInt(rtMesh.substring(4, 5));
        const rtM6 = parseInt(rtMesh.substring(5, 6));
        const rtM7 = parseInt(rtMesh.substring(6, 7));
        const rtM8 = parseInt(rtMesh.substring(7, 8));
        const hSize = (rtM34 - lbM34) * 48 + (rtM6 - lbM6) * 8 + (rtM8 - lbM8) + 1;
        const vSize = (rtM12 - lbM12) * 48 + (rtM5 - lbM5) * 8 + (rtM7 - lbM7) + 1;
        const meshSize = hSize * vSize; // 图幅数

        // 从左下角开始计算图幅
        const allMesh = new Array(meshSize);

        let targetM12 = 0;
        let targetM34 = 0;
        let targetM5 = 0;
        let targetM6 = 0;
        let targetM7 = 0;
        let targetM8 = 0;
        for (let v = 0; v < vSize; v++) {
            targetM7 = (lbM7 - 1 + v) % 8 + 1;
            const midM5 = parseInt(lbM5 + ((lbM7 - 1 + v) / 8) - 1);
            targetM5 = midM5 % 6 + 1;
            targetM12 = parseInt(lbM12 + midM5 / 6);

            for (let h = 0; h < hSize; h++) {
                targetM8 = (lbM8 - 1 + h) % 8 + 1;
                const midM6 = parseInt(lbM6 + ((lbM8 - 1 + h) / 8) - 1);
                targetM6 = midM6 % 6 + 1;
                targetM34 = parseInt(lbM34 + midM6 / 6);
                allMesh[v * hSize + h] = `${targetM12}${targetM34}${targetM5}${targetM6}${targetM7}${targetM8}`;
            }
        }

        return allMesh;
    }

    /**
     * 多个2.5w图幅的外包矩形
     * @param  {array} meshes 图幅号数组
     * @return {array}        外包矩形坐标数组
     */
    meshs2Rect25k(meshes) {
        if (meshes || meshes.length === 0) {
            return null;
        }

        if (meshes.length === 1) {
            return this.mesh2Rect25k(meshes[0]);
        }

        const xSet = new Set(); // 所有经度集合
        const ySet = new Set(); // 所有维度集合
        for (const mesh of meshes) {
            const meshStr = mesh.toString();
            const xStr = meshStr.substring(0, 2) + meshStr.substring(4, 5) + meshStr.substring(6, 7);
            const yStr = meshStr.substring(2, 4) + meshStr.substring(5, 6) + meshStr.substring(7, 8);
            xSet.add(parseInt(xStr));
            ySet.add(parseInt(yStr));
        }

        const x0Str = Math.min(...xSet).toString();
        const x1Str = Math.max(...xSet).toString();
        const y0Str = Math.min(...ySet).toString();
        const y1Str = Math.max(...ySet).toString();

        const minMesh = x0Str.substring(0, 2) + y0Str.substring(0, 2) + x0Str.substring(2, 3) + y0Str.substring(2, 3) + x0Str.substring(3) + y0Str.substring(3);
        const maxMesh = x1Str.substring(0, 2) + y1Str.substring(0, 2) + x1Str.substring(2, 3) + y1Str.substring(2, 3) + x1Str.substring(3) + y1Str.substring(3);

        const minPoint = this.mesh2Rect25k(minMesh);
        const maxPoint = this.mesh2Rect25k(maxMesh);

        return [minPoint[0], minPoint[1], maxPoint[2], maxPoint[3]];
    }
}
